import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { get as storageGet, remove as storageRemove, set as storageSet } from '../common/storage';
import { emptySettings, isConfigured, Settings } from './models';

const SETTINGS_KEY = 'nuxni-time-tracker:settings';

const loadSettings = (): Settings => {
    const raw = storageGet<Partial<Settings>>(SETTINGS_KEY);
    if (!raw) {
        return emptySettings;
    }
    return {
        pat: raw.pat ?? emptySettings.pat,
        org: raw.org ?? emptySettings.org,
        project: raw.project ?? emptySettings.project,
    };
};

const saveSettings = (settings: Settings): void => storageSet(SETTINGS_KEY, settings);
const clearSettings = (): void => storageRemove(SETTINGS_KEY);

type SettingsContextValue = {
    settings: Settings;
    configured: boolean;
    save: (next: Settings) => void;
    logout: () => void;
};

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export const SettingsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [settings, setSettings] = useState<Settings>(() => loadSettings());

    const save = useCallback((next: Settings) => {
        saveSettings(next);
        setSettings(next);
    }, []);

    const logout = useCallback(() => {
        clearSettings();
        setSettings(emptySettings);
    }, []);

    const value = useMemo(
        () => ({
            settings,
            configured: isConfigured(settings),
            save,
            logout,
        }),
        [settings, save, logout],
    );

    return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): SettingsContextValue => {
    const ctx = useContext(SettingsContext);
    if (!ctx) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return ctx;
};
